import httpClient from "../http/http-client";
import { ICapitalGood, IFootprintVersion, IMaterialFactor, IProductDocument } from './capital-good.model';
import { IEpd, IEpdUploadResult } from './epd.model';

export default class CapitalGoodService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAll(queryString?: string): Promise<ICapitalGood[]> {
    return this.http.get(`capital-good${queryString ? queryString : ''}`)
      .then(response => {
        return response.data.rows
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAllActive(queryString?: string): Promise<ICapitalGood[]> {
    return this.http.get(`capital-good?active=true${queryString ? queryString : ''}`)
      .then(response => {
        return response.data.rows
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAllWithPagination(contactSupplierId: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', typeOfGood?: string,): Promise<{ count: number, rows: ICapitalGood[] }> {
    return this.http.get(`capital-good?contactSupplierId=${contactSupplierId}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${typeOfGood ? `&typeOfGood=${typeOfGood}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getAllActiveWithPagination(queryString: string = '', page: number = 0, perPage: number = 12, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: ICapitalGood[] }> {
    return this.http.get(`capital-good?active=true&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getOne(capitalGoodId: string): Promise<ICapitalGood> {
    return this.http.get(`capital-good/${capitalGoodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async create(formData: FormData): Promise<ICapitalGood> {
    return this.http.post(`capital-good`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async update(capitalGoodId: string, formData: FormData): Promise<ICapitalGood> {
    return this.http.patch(`capital-good/${capitalGoodId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deactivate(capitalGoodId: string): Promise<ICapitalGood> {
    return this.http.patch(`capital-good/${capitalGoodId}/deactivate`, { capitalGoodId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async remove(capitalGoodId: string): Promise<ICapitalGood> {
    return this.http.delete(`capital-good/${capitalGoodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // --- EPD (Environmental Product Declaration) attachments ---

  async getEpds(capitalGoodId: string): Promise<IEpd[]> {
    return this.http.get(`capital-good/${capitalGoodId}/epd`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async uploadEpd(capitalGoodId: string, formData: FormData): Promise<IEpdUploadResult> {
    return this.http.post(`capital-good/${capitalGoodId}/epd`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteEpd(capitalGoodId: string, epdId: string): Promise<{ message: string }> {
    return this.http.delete(`capital-good/${capitalGoodId}/epd/${epdId}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async downloadEpd(capitalGoodId: string, epdId: string): Promise<Blob> {
    return this.http.get(`capital-good/${capitalGoodId}/epd/${epdId}/download`, { responseType: 'blob' })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Typed product documents (LCA reports, certificates, test reports) ---

  async getDocuments(capitalGoodId: string): Promise<IProductDocument[]> {
    return this.http.get(`capital-good/${capitalGoodId}/document`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async uploadDocument(capitalGoodId: string, formData: FormData): Promise<IProductDocument> {
    return this.http.post(`capital-good/${capitalGoodId}/document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteDocument(capitalGoodId: string, documentId: string): Promise<{ message: string }> {
    return this.http.delete(`capital-good/${capitalGoodId}/document/${documentId}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async downloadDocument(capitalGoodId: string, documentId: string): Promise<Blob> {
    return this.http.get(`capital-good/${capitalGoodId}/document/${documentId}/download`, { responseType: 'blob' })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Footprint version history ---

  async getFootprintVersions(capitalGoodId: string): Promise<IFootprintVersion[]> {
    return this.http.get(`capital-good/${capitalGoodId}/footprint-versions`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- Product footprint pack export ---

  async downloadFootprintPack(capitalGoodId: string, format: 'pdf' | 'xlsx'): Promise<Blob> {
    return this.http.get(`capital-good/${capitalGoodId}/footprint-pack/${format}`, { responseType: 'blob' })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // --- DEFRA material factor library ---

  async searchMaterialFactors(search: string): Promise<IMaterialFactor[]> {
    return this.http.get(`conversion/material-factors?search=${encodeURIComponent(search)}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }
}